import { Button, Heading, OTPField } from '@invisionag/components';
import { Trans, useTranslation } from '@invisionag/next/i18n';
import { useRouter, useSearchParams } from 'next/navigation';
import { Result } from '@invisionag/next/result';
import { CenteredContainer } from '../../_components/centered-container';
import { SetView } from './page';
import { getRedirectUrl } from '@/lib/utils';
import { Form, FormResult, FormMessage, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { unreachable } from '@/lib/exhaustive';

export function OTPForm({ setView }: { setView: SetView }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('return_url');

  async function verifyOtp(formData: FormData): Promise<FormResult> {
    const otp = assertString(formData.get('otp'));

    if (!otp) {
      return Result.Err({
        validationErrors: {
          otp: t('login.otp-form.field.otp.validation.valueMissing'),
        },
      });
    }

    const result = await Cortex.verifyOtp(otp);

    if (!result.ok) {
      switch (result.err) {
        case 'invalid-otp':
          return Result.Err({
            message: t(`login.otp-form.error.${result.err}`),
          });
        case 'timeout':
          setView('login', {
            message: t(`login.otp-form.error.${result.err}`),
          });
          return Result.Err({});
        default:
          unreachable('error', result.err);
      }
    }

    router.replace(getRedirectUrl(returnUrl));
    return Result.Ok();
  }

  return (
    <CenteredContainer>
      <Heading level={1} className="text-center">
        {t('login.otp-form.heading')}
      </Heading>
      <Form action={verifyOtp} className="grid gap-4">
        <div className="grid gap-4">
          <OTPField
            isRequired
            name="otp"
            label={t('login.otp-form.field.otp.label')}
            description={t('login.otp-form.field.otp.description')}
            autoFocus
          />
          <div>
            <Trans
              i18n={i18n}
              i18nKey="login.otp-form.field.otp.recovery-hint"
              components={[
                <Button
                  key="use-recovery-code"
                  variant="link-like"
                  onPress={() => setView('recoveryCode')}
                />,
              ]}
            />
          </div>
        </div>
        <FormMessage />
        <SubmitButton className="w-full">
          {t('login.otp-form.submit')}
        </SubmitButton>
        <Button
          variant="link-like"
          onPress={() => setView('login')}
          className="justify-self-center"
        >
          {t('login.otp-form.go-to-login')}
        </Button>
      </Form>
    </CenteredContainer>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}
